import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mockStats as mockStatsEn } from "@/mocks/projects";
import { mockStats as mockStatsPs } from "@/mocks/ps/projects";
import { mockStats as mockStatsDa } from "@/mocks/da/projects";

const localizedStats: Record<string, typeof mockStatsEn> = {
  en: mockStatsEn,
  fr: mockStatsEn,
  de: mockStatsEn,
  ps: mockStatsPs,
  da: mockStatsDa,
};

export default function HeroSection() {
  const { t, i18n } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const mockStats = localizedStats[i18n.language] || mockStatsEn;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    const timer = setTimeout(() => {
      el.style.transition = "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    children.forEach((child, i) => {
      child.style.opacity = "0";
      child.style.transform = "translateY(20px)";
      setTimeout(() => {
        child.style.transition = "opacity 0.6s ease, transform 0.6s ease";
        child.style.opacity = "1";
        child.style.transform = "translateY(0)";
      }, 600 + i * 120);
    });
  }, []);

  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Afghan%20children%20and%20community%20members%20in%20a%20vibrant%20outdoor%20setting%20with%20mountains%20in%20background%2C%20warm%20golden%20hour%20lighting%2C%20hopeful%20and%20uplifting%20atmosphere%2C%20rich%20colors%20with%20greens%20and%20earth%20tones%2C%20wide%20landscape%20photography&width=1600&height=900&seq=hero1&orientation=landscape"
          alt="CAF Mission"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2838]/85 via-[#1B2838]/60 to-[#1B2838]/30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="max-w-2xl" ref={contentRef}>
          <div className="inline-flex items-center gap-2 bg-[#4ECDC4]/20 border border-[#4ECDC4]/40 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#4ECDC4] animate-pulse"></span>
            <span className="text-[#4ECDC4] text-xs font-semibold uppercase tracking-widest">
              {t("hero.badge")}
            </span>
          </div>

          <h1
            className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("hero.title")}
          </h1>

          <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <Link
              to="/donate"
              className="px-8 py-4 bg-[#4ECDC4] text-[#1B2838] rounded-full font-bold text-base whitespace-nowrap cursor-pointer hover:bg-[#3dbdb5] transition-all flex items-center justify-center gap-2"
            >
              <i className="ri-heart-fill"></i>
              {t("hero.cta.donate")}
            </Link>
            <Link
              to="/mission"
              className="px-8 py-4 bg-white/10 border border-white/30 text-white rounded-full font-semibold text-base whitespace-nowrap cursor-pointer hover:bg-white/20 transition-all flex items-center justify-center gap-2 backdrop-blur-sm"
            >
              {t("hero.cta.learn")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" ref={statsRef}>
            {mockStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-4 text-center"
              >
                <div className="text-2xl font-bold text-[#4ECDC4]">{stat.value}</div>
                <div className="text-white/70 text-xs mt-1 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 animate-bounce">
        <span className="text-xs uppercase tracking-widest">{t("hero.scroll")}</span>
        <i className="ri-arrow-down-line"></i>
      </div>
    </section>
  );
}